'use client'

import { NavigationItems } from 'constants/navigationItems'
import ThemeColor from 'constants/themeColors'
import { useDeviceType } from 'hooks/useDeviceType'
import { AnimatePresence, motion, Variants } from 'motion/react'
import Link from 'next/link'
import Close from 'public/assets/close-light.svg'
import HamburgerWhite from 'public/assets/hamburger-white.svg'
import Hamburger from 'public/assets/hamburger.svg'
import { useState } from 'react'
import styles from './NavigationMenu.module.scss'

type NavigationMenuProps = {
  themeColor: string
  homepage?: boolean
}

const NavigationMenu = ({ themeColor, homepage = false }: NavigationMenuProps) => {
  const [open, setOpen] = useState(homepage ? true : false)
  const [hoveredItem, setHoveredItem] = useState(NavigationItems.Pixion)

  const { isMobile, isTablet } = useDeviceType()

  const variants: Variants = {
    hidden: {
      scaleY: 0,
      transformOrigin: 'top',
      transition: {
        duration: 0.4,
        ease: 'circIn',
      },
    },
    visible: {
      scaleY: 1,
      transition: {
        duration: 0.4,
        ease: 'circIn',
      },
    },
  }

  const childrenVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 80,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0.6, duration: 0.2 },
    },
  }

  const childrenAnimation = {
    variants: childrenVariants,
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
  }

  function getOpacityOnHover(itemKey: string) {
    return hoveredItem.key === 'pixion' ? 1 : hoveredItem.key === itemKey ? 1 : 0.3
  }

  function toggleNavigation() {
    if (open) {
      document.querySelector("meta[name='theme-color']")!.setAttribute('content', themeColor)
      document.body.style.overflow = 'visible'
      setOpen(false)
    } else {
      document.querySelector("meta[name='theme-color']")!.setAttribute('content', ThemeColor.Navigation)
      document.body.style.overflow = 'hidden'
      setOpen(true)
    }
  }

  return (
    <>
      {homepage ? (
        <HamburgerWhite className={styles.hamburger} onClick={toggleNavigation} />
      ) : (
        <Hamburger className={styles.hamburger} onClick={toggleNavigation} />
      )}
      <AnimatePresence mode='wait'>
        {open && (
          <motion.div className={styles.menu} variants={variants} layoutRoot initial={{ scaleY: homepage ? 1 : 0 }} animate='visible' exit='hidden'>
            <motion.div className={styles.close} {...childrenAnimation}>
              <Close onClick={toggleNavigation} />
            </motion.div>

            <motion.div className={styles.content} {...childrenAnimation}>
              <ul className={styles.links} onMouseLeave={() => setHoveredItem(NavigationItems.Pixion)}>
                {Object.values(NavigationItems)
                  .filter((x) => x.displayName !== '')
                  .map((item) => (
                    <li key={item.key} className={styles.link}>
                      <Link
                        href={item.href}
                        prefetch={item.key === 'blog' ? false : undefined}
                        style={{ opacity: getOpacityOnHover(item.key) }}
                        onMouseOver={() => setHoveredItem(item)}
                        onClick={toggleNavigation}
                      >
                        {item.displayName}
                      </Link>
                    </li>
                  ))}
              </ul>

              <motion.span
                className={styles.motto}
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { delay: 0.8, duration: 0.3 },
                }}
                exit={{
                  opacity: 0,
                  y: 20,
                  transition: { delay: 0, duration: 0 },
                }}
              >
                Building beautiful tech :P
              </motion.span>
            </motion.div>

            {isMobile && <motion.img src={hoveredItem.srcMobile} style={{ zIndex: -1 }} draggable='false' {...childrenAnimation} />}
            {isTablet && <motion.img src={hoveredItem.srcTablet} style={{ zIndex: -1 }} draggable='false' {...childrenAnimation} />}
            {!isTablet && !isMobile && <motion.img src={hoveredItem.src} style={{ zIndex: -1 }} draggable='false' {...childrenAnimation} />}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default NavigationMenu
